import React from "react";
import { parseDayOfWeek } from "../MessageRevivers";
import {
  BroadcastPattern,
  OnboardingVisitInstanceId,
  PublicOnboardingVisitDetailsTypes,
  PublicVisitBroadcastDetailsTypes,
  VisitDatesParams,
  VisitInstanceId,
} from "../messages";
import { STRINGS } from "../strings";
import { globalize, translate } from "../translate";
import { HashSet } from "../utils/HashSet";
import { CheckedVisitData } from "./AvailableVisitActivity";
import AvailableWeeklyTemplateShift from "./AvailableWeeklyTemplateShift";
import { AppCheckbox } from "./Generic/AppCheckbox";
import { AppList, AppListItem } from "./Generic/AppList";
import { If } from "./Generic/If";
import { CheckedOnboardingVisitData } from "./OnboardingVisitActivity";

type VisitInstancesPatternDetails =
  | {
      date: VisitDatesParams;
      visitInstanceId: VisitInstanceId;
    }
  | {
      date: VisitDatesParams;
      visitInstanceId: OnboardingVisitInstanceId;
    };

interface Props {
  allowMultipleCaregivers: boolean;
  pattern: PublicVisitBroadcastDetailsTypes.Pattern | PublicOnboardingVisitDetailsTypes.Pattern;
  checkedPatterns: HashSet<string>;
  requestedVisitBatch: boolean;
  areAllShiftsChecked: boolean;
  onCheckVisit:
    | ((visitData: CheckedVisitData) => void)
    | ((visitData: CheckedOnboardingVisitData) => void);
}

const AvailableWeeklyTemplateDates = (props: Props) => {
  const shiftsOfDayOfWeek = formatDaysOfWeek(
    props.pattern.weeklyPattern.daysOfWeek,
    props.pattern.visitInstances
  );
  return (
    <AppList bordered={true}>
      <If condition={props.allowMultipleCaregivers && shiftsOfDayOfWeek.length > 1}>
        <AppListItem
          leading={
            <AppCheckbox
              style={{ marginInlineEnd: 15 }}
              checked={props.areAllShiftsChecked}
              onClick={() => props.onCheckVisit({ type: "AllShifts" })}
              disabled={props.requestedVisitBatch}
            />
          }
          title={translate(STRINGS.SELECT_ALL)}
        />
      </If>
      {shiftsOfDayOfWeek
        .sort((s1, s2) => parseDayOfWeek(s1.key).ordinal() - parseDayOfWeek(s2.key).ordinal())
        .map((dayOfWeek) => (
          <AvailableWeeklyTemplateShift
            key={dayOfWeek.key}
            checkedPatterns={props.checkedPatterns}
            dayOfWeek={dayOfWeek}
            isMultiShift={props.allowMultipleCaregivers && shiftsOfDayOfWeek.length > 1}
            requestedVisitBatch={props.requestedVisitBatch}
            onCheckVisit={props.onCheckVisit}
          />
        ))}
    </AppList>
  );
};

export default AvailableWeeklyTemplateDates;

function asDefined<T>(val: T | undefined): T {
  return val as unknown as T;
}

function formatDaysOfWeek(
  daysOfWeek: BroadcastPattern["daysOfWeek"],
  visitInstances: VisitInstancesPatternDetails[]
) {
  return Object.entries(daysOfWeek).map(([day, shifts]) => {
    return {
      key: day,
      displayDay: globalize().formatDayOfWeek(parseDayOfWeek(day), false),
      shifts: asDefined(shifts),
      visitInstancesCount: visitInstances.filter(
        (instanceDetails) => instanceDetails.date.startDateTime.dayOfWeek().toString() === day
      ).length,
    };
  });
}
